
import { useCallback, useState, useEffect } from 'react';
import { 
  checkAllTeacherNotifications,
  TeacherNotification, 
  markNotificationAsRead,
  getTeacherNotifications
} from '@/services/db/teachers';
import { useAttendancePatternNotifications } from "./useAttendancePatternNotifications";
import { checkSalaryReminders } from '@/services/db/teachers/notifications/payment-notifications';

export const useTeacherNotifications = () => {
  const [notifications, setNotifications] = useState<TeacherNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedNotifications = await getTeacherNotifications();
      setNotifications(fetchedNotifications);
      setUnreadCount(fetchedNotifications.filter(n => !n.isRead).length);
      return fetchedNotifications;
    } catch (error) {
      console.error('Error fetching teacher notifications:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);
  
  const { checkAttendancePatterns } = useAttendancePatternNotifications();

  // Enhanced function to check for salary reminders
  const checkSalaryPaymentReminders = useCallback(async () => {
    try {
      const salaryNotifications = await checkSalaryReminders();
      if (salaryNotifications.length > 0) {
        await fetchNotifications();  // Refresh notifications if new ones were created
      }
      return salaryNotifications;
    } catch (error) {
      console.error('Error checking salary reminders:', error);
      return [];
    }
  }, [fetchNotifications]);

  // Enhance checkNotifications: also check for attendance patterns after main checks
  const checkNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const newNotifications = await checkAllTeacherNotifications();

      // Check for salary payments
      await checkSalaryPaymentReminders();

      // --- check for attendance patterns/trends ---
      // Pass an empty array if no attendance records are available
      checkAttendancePatterns([]);

      if (newNotifications.length > 0) {
        await fetchNotifications();
      }
      return newNotifications;
    } catch (error) {
      console.error("Error checking teacher notifications:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [fetchNotifications, checkAttendancePatterns, checkSalaryPaymentReminders]);
  
  // Mark a notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, isRead: true, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }, []);
  
  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const promises = notifications
        .filter(n => !n.isRead)
        .map(n => markNotificationAsRead(n.id));
      
      await Promise.all(promises);
      
      setNotifications(prev => 
        prev.map(n => ({ ...n, isRead: true, read: true }))
      );
      
      setUnreadCount(0);
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  }, [notifications]);
  
  // Initialize by fetching notifications and setting up periodic checks
  useEffect(() => {
    fetchNotifications();

    // Check for salary notifications every hour
    const salaryCheckInterval = setInterval(() => {
      checkSalaryPaymentReminders();
    }, 60 * 60 * 1000); // 1 hour

    return () => {
      clearInterval(salaryCheckInterval);
    };
  }, [fetchNotifications, checkSalaryPaymentReminders]);
  
  // Initialize by fetching notifications
  const initialize = useCallback(async () => {
    await fetchNotifications();
    // Also check for notifications immediately on initialization
    await checkNotifications();
  }, [fetchNotifications, checkNotifications]);
  
  return {
    notifications,
    loading,
    unreadCount,
    fetchNotifications,
    checkNotifications,
    markAsRead,
    markAllAsRead,
    initialize,
    checkSalaryReminders: checkSalaryPaymentReminders
  };
};
